// Pure ranking logic, ported from js/renderers-rankings.js. The Rankings widget ranks
// the whole population for one metric in one season — every player who passes the
// active compare-against filters AND has a real, non-zero recorded value for that
// metric that season, sorted by value (descending). Reuses the StatsFilterKit engine
// (matchesFilters / resolveTotalValue) and getPlayerShortName. Kept pure (population +
// categories passed in) so it's testable and unchanged at the Track-2 cutover.

import type { ProtoMetricCategory, ProtoPlayerMetric, ProtoRankingEntry } from "@catbento/shared";
import type { RawPlayerRecord } from "./snapshot.js";
import { getPlayerShortName } from "./player-utils.js";
import { playerIdOf } from "./players.js";
import { seasonSortKey } from "./league.js";
import {
  matchesFilters,
  resolveTotalValue,
  type ActiveFilters,
  type KitPlayer,
  type KitMetric,
  type RangeFilterConfig,
} from "../engines/stats-filter-kit.js";

type Players = Record<string, RawPlayerRecord>;

export interface RankingMetricResult {
  metric: ProtoPlayerMetric;
  category: ProtoMetricCategory;
}

/** Resolve a (categoryId, metricKey) pair to its metric + category, or null if either
 *  is absent/unknown — exactly renderers-rankings.js's findMetric. */
export function findRankingMetric(
  categories: readonly ProtoMetricCategory[],
  categoryId: string | null | undefined,
  metricKey: string | null | undefined,
): RankingMetricResult | null {
  if (!categoryId || !metricKey) return null;
  const category = categories.find((c) => c.id === categoryId);
  if (!category) return null;
  for (const sub of category.subcategories) {
    const metric = sub.playerMetrics.find((m) => m.key === metricKey);
    if (metric) return { metric, category };
  }
  return null;
}

/** A category's own first metric — the default a "… Rank" tile opens on (it seeds only
 *  the category, not a specific metric). renderers-rankings.js:216. */
export function firstRankingMetricKey(
  categories: readonly ProtoMetricCategory[],
  categoryId: string | null | undefined,
): string | null {
  const category = categoryId ? categories.find((c) => c.id === categoryId) : undefined;
  return category?.subcategories[0]?.playerMetrics[0]?.key ?? null;
}

/**
 * Every distinct season code with at least one player's real recorded stats, across the
 * whole population, newest-first. Not one player's seasons — a ranking spans everyone
 * (renderers-rankings.js:101). Newest-first via seasonSortKey (a "5"-prefixed European
 * code's real end-year is year+1, so it interleaves correctly with calendar-year codes).
 */
export function rankingSeasonCodes(players: Players): string[] {
  const codes = new Set<string>();
  for (const pl of Object.values(players)) {
    const seasons = pl.seasons ?? {};
    for (const code of Object.keys(seasons)) {
      const stats = seasons[code]?.stats ?? {};
      if (Object.keys(stats).length > 0) codes.add(code);
    }
  }
  return Array.from(codes).sort((a, b) => seasonSortKey(b) - seasonSortKey(a));
}

/**
 * The ranked leaderboard. A player is included only if they pass every active filter
 * AND have a real, non-zero recorded value for this metric in this season — a recorded
 * 0 (or absence) drops them rather than faking a rank (renderers-rankings.js:404). Value
 * is the Total (per90 × nineties, when in total mode) or the raw per-90 rate. Descending.
 */
export function computeRanking(
  players: Players,
  metric: KitMetric,
  seasonCode: string,
  useTotal: boolean,
  active: ActiveFilters,
  refPlayer: KitPlayer,
  rangeFilters: RangeFilterConfig,
): ProtoRankingEntry[] {
  const entries: ProtoRankingEntry[] = [];
  for (const key of Object.keys(players)) {
    const pl = players[key];
    if (!pl) continue;
    // RawPlayerRecord is structurally the KitPlayer the engine reads (its `stats` lives
    // under the record's index signature); cast rather than restate the shape.
    if (!matchesFilters(pl as unknown as KitPlayer, active, refPlayer, rangeFilters)) continue;
    const seasonStats = pl.seasons?.[seasonCode]?.stats ?? {};
    const value = useTotal ? resolveTotalValue(seasonStats, metric) : seasonStats[metric.key];
    if (value == null || Number.isNaN(value) || value === 0) continue;
    entries.push({
      playerId: playerIdOf(pl),
      name: pl.name ?? key,
      shortName: getPlayerShortName(pl),
      team: pl.team ?? null,
      competition: pl.competition ?? null,
      value,
    });
  }
  entries.sort((a, b) => b.value - a.value);
  return entries;
}
